// Spiegelt backend/app/services/validierung.py. Das Backend prüft immer noch einmal.

export const KWP_MAX = 1000;

const IBAN_LAENGEN: Record<string, number> = {
  DE: 22, AT: 20, CH: 21, LI: 21, NL: 18, BE: 16, LU: 20, FR: 27,
  IT: 27, ES: 24, PL: 28, DK: 18, CZ: 24, SE: 24, FI: 18, IE: 22,
};

export function normalisiereIban(wert: string): string {
  return wert.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

export function formatiereIban(wert: string): string {
  return normalisiereIban(wert).replace(/(.{4})/g, "$1 ").trim();
}

function ibanPruefsummeGueltig(iban: string): boolean {
  const umgestellt = iban.slice(4) + iban.slice(0, 4);
  let rest = 0;
  for (const zeichen of umgestellt) {
    const ziffern = /\d/.test(zeichen) ? zeichen : String(zeichen.charCodeAt(0) - 55);
    for (const z of ziffern) rest = (rest * 10 + Number(z)) % 97;
  }
  return rest === 1;
}

export function pruefeIban(roh: string): string | null {
  const iban = normalisiereIban(roh);
  if (!iban) return null;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return "IBAN hat kein gültiges Format (z. B. DE89 3704 0044 0532 0130 00)";
  const erwartet = IBAN_LAENGEN[iban.slice(0, 2)];
  if (erwartet && iban.length !== erwartet) return `IBAN für ${iban.slice(0, 2)} muss ${erwartet} Zeichen haben (hat ${iban.length})`;
  if (!ibanPruefsummeGueltig(iban)) return "IBAN-Prüfsumme ist ungültig – bitte Ziffern kontrollieren";
  return null;
}

/** "9,86" / "9.86" / "9,86 kWp" → 9.86; leer → null; unlesbar → NaN */
export function parseLeistung(roh: string): number | null {
  const text = roh.trim().toLowerCase().replace(/\s*kwp?$/, "");
  if (!text) return null;
  let zahl = text.replace(/\s/g, "");
  if (zahl.includes(",") && zahl.includes(".")) {
    zahl = zahl.lastIndexOf(",") > zahl.lastIndexOf(".") ? zahl.replace(/\./g, "").replace(",", ".") : zahl.replace(/,/g, "");
  } else {
    zahl = zahl.replace(",", ".");
  }
  return /^-?\d+(\.\d+)?$/.test(zahl) ? Number(zahl) : Number.NaN;
}

export function pruefeLeistung(roh: string): string | null {
  const wert = parseLeistung(roh);
  if (wert === null) return null;
  if (Number.isNaN(wert)) return "Leistung muss eine Zahl sein (z. B. 9,86)";
  if (wert <= 0 || wert > KWP_MAX) {
    return `Leistung muss zwischen 0 und ${KWP_MAX} kWp liegen${wert > KWP_MAX ? " – evtl. in Wp statt kWp angegeben?" : ""}`;
  }
  return null;
}

export function pruefePlz(roh: string): string | null {
  const plz = roh.replace(/\s/g, "");
  if (!plz) return null;
  return /^\d{5}$/.test(plz) ? null : "PLZ muss genau 5 Ziffern haben";
}

export function pruefeZaehlernummer(roh: string): string | null {
  const nummer = roh.replace(/\s/g, "").toUpperCase();
  if (!nummer) return null;
  return /^[A-Z0-9][A-Z0-9\-./]{2,39}$/.test(nummer) ? null : "Zählernummer: 3–40 Zeichen, nur Buchstaben, Ziffern, - . /";
}

export function pruefeZerezId(roh: string): string | null {
  const id = roh.trim();
  if (!id) return null;
  return /^[A-Za-z0-9][A-Za-z0-9\-_./]{2,49}$/.test(id) ? null : "ZEREZ-ID: 3–50 Zeichen, nur Buchstaben, Ziffern, - _ . /";
}

export function pruefeVollmachtDatum(roh: string, heute: Date = new Date()): string | null {
  if (!roh) return null;
  const datum = new Date(`${roh}T00:00:00`);
  if (Number.isNaN(datum.getTime())) return "Ungültiges Datum";
  const morgen = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() + 1);
  if (datum > morgen) return "Vollmacht-Datum liegt in der Zukunft";
  if (datum < new Date("2000-01-01T00:00:00")) return "Vollmacht-Datum ist unplausibel (vor 2000)";
  return null;
}

export function pruefeTextlaenge(roh: string, max = 200): string | null {
  return roh.trim().length > max ? `Höchstens ${max} Zeichen` : null;
}

export function pruefeEmail(roh: string): string | null {
  if (!roh.trim()) return "E-Mail ist ein Pflichtfeld";
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(roh.trim()) ? null : "Keine gültige E-Mail-Adresse";
}
